import { Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import logo from "@/assets/logo.png";
import {
  BUSINESS_NAME,
  BUSINESS_NAME_LEGAL,
  PHONE,
  PHONE_LINK,
  EMAIL,
  WEST_VALLEY_CITIES,
  PLUMBING_SERVICES,
  PURIFICATION_SERVICES,
} from "@/lib/constants";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-card border-t border-border">
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/">
              <img
                src={logo}
                alt={`${BUSINESS_NAME} Logo`}
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-muted-foreground text-sm">
              Veteran-owned and family-operated, serving Tolleson and the West Valley 
              with honest pricing and American quality. Licensed, bonded, and insured. 
              24/7 scheduling available via our intelligent voice assistant.
            </p>
            <div className="space-y-2">
              <a
                href={PHONE_LINK}
                className="flex items-center gap-2 text-primary hover:text-accent transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="font-semibold">{PHONE}</span>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors text-sm"
              >
                <Mail className="h-4 w-4" />
                {EMAIL}
              </a>
            </div>
          </div>

          {/* Plumbing Services */}
          <div>
            <h3 className="text-lg font-bold text-foreground mb-4">
              Plumbing Services
            </h3>
            <ul className="space-y-2">
              {PLUMBING_SERVICES.slice(0, 6).map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/services/${service.slug}/`}
                    className="text-sm text-foreground/70 hover:text-primary transition-colors"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/services/"
                  className="text-sm text-primary hover:text-accent transition-colors font-medium"
                >
                  View All Services →
                </Link>
              </li>
            </ul>
          </div>

          {/* Water Purification */}
          <div>
            <h3 className="text-lg font-bold text-foreground mb-4">
              Water Purification
            </h3>
            <ul className="space-y-2">
              {PURIFICATION_SERVICES.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/water-purification/${service.slug}/`}
                    className="text-sm text-foreground/70 hover:text-primary transition-colors"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="text-lg font-bold text-foreground mt-6 mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about/"
                  className="text-sm text-foreground/70 hover:text-primary transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/reviews/"
                  className="text-sm text-foreground/70 hover:text-primary transition-colors"
                >
                  Customer Reviews
                </Link>
              </li>
              <li>
                <Link
                  to="/contact/"
                  className="text-sm text-foreground/70 hover:text-primary transition-colors"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy/"
                  className="text-sm text-foreground/70 hover:text-primary transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-of-service/"
                  className="text-sm text-foreground/70 hover:text-primary transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="text-lg font-bold text-foreground mb-4">
              Service Areas
            </h3>
            <ul className="space-y-2">
              {WEST_VALLEY_CITIES.slice(0, 9).map((city) => {
                const slug = `${city.toLowerCase().replace(/\s+/g, "-")}-az`;
                return (
                  <li key={city}>
                    <Link
                      to={`/service-areas/${slug}/`}
                      className="text-sm text-foreground/70 hover:text-primary transition-colors"
                    >
                      {city}, AZ
                    </Link>
                  </li>
                );
              })}
              <li>
                <Link
                  to="/service-areas/"
                  className="text-sm text-primary hover:text-accent transition-colors font-medium"
                >
                  View All Areas →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {currentYear} {BUSINESS_NAME_LEGAL}. All rights reserved. Licensed,
              Bonded & Insured. <span className="font-semibold text-foreground/80">ROC 362827</span>
            </p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Link to="/privacy-policy/" className="hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <span>|</span>
              <Link to="/terms-of-service/" className="hover:text-primary transition-colors">
                Terms of Service
              </Link>
              <span>|</span>
              <span>Serving the West Valley & West Phoenix</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
